import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, RefreshCw, Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { coverageStateSchema } from '../../lib/schemas';
import { getSocket } from '../../lib/socket';
import type { CoverageAssignment, CoverageState } from '../../types';
import { getTeamCoverage } from './teamApi';
import { ModulePage } from './ModulePage';

interface TeamStatusProps {
  currentUserId: number;
}

const statuses = {
  active: { className: 'active', label: 'In the room' },
  waiting: { className: 'waiting', label: 'Joining or waiting to be admitted' },
  error: { className: 'error', label: 'Connection problem' },
} as const;

export function TeamStatus({ currentUserId }: TeamStatusProps) {
  const queryClient = useQueryClient();
  const coverage = useQuery({
    queryKey: queryKeys.teamCoverage,
    queryFn: ({ signal }) => getTeamCoverage(signal),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    const socket = getSocket();
    const requestCoverage = () => socket.emit('request_coverage');
    const updateCoverage = (payload: CoverageState) => {
      const parsed = coverageStateSchema.safeParse(payload);
      if (parsed.success) queryClient.setQueryData(queryKeys.teamCoverage, parsed.data);
    };

    socket.on('connect', requestCoverage);
    socket.on('coverage_update', updateCoverage);
    requestCoverage();
    return () => {
      socket.off('connect', requestCoverage);
      socket.off('coverage_update', updateCoverage);
    };
  }, [queryClient]);

  const state = coverage.data;
  const assignments = useMemo(() => {
    const byMemberAndRoom = new Map<string, CoverageAssignment>();
    for (const assignment of state?.coverage ?? []) {
      byMemberAndRoom.set(`${assignment.user_id}:${assignment.event_id}`, assignment);
    }
    return byMemberAndRoom;
  }, [state?.coverage]);
  const onlineCount = state?.members.filter(member => member.online).length ?? 0;

  return (
    <ModulePage
      id="team-status-title"
      title="Team"
      className="team-status"
      meta={<span className="team-status__count">{onlineCount} online</span>}
      actions={(
        <button className="icon-button team-status__refresh" type="button" title="Refresh team status" aria-label="Refresh team status" disabled={coverage.isFetching} onClick={() => void coverage.refetch()}>
          <RefreshCw className={coverage.isFetching ? 'spin' : ''} size={17} />
        </button>
      )}
    >
      <div className="team-status__legend" aria-label="Room status legend">
          <Legend status="active" label="In the room" />
          <Legend status="waiting" label="Joining / waiting" />
          <Legend status="error" label="Problem" />
          <Legend status="none" label="Not in this room" />
        </div>

        {coverage.isPending ? (
          <div className="native-module__state" role="status"><LoaderCircle className="spin" size={20} />Loading team status</div>
        ) : null}

        {coverage.isError ? (
          <div className="native-module__state native-module__state--error" role="alert">
            <strong>Team status could not be loaded</strong>
            <span>{errorMessage(coverage.error, 'Try refreshing the board.')}</span>
            <button className="secondary-button" type="button" onClick={() => void coverage.refetch()}>Try again</button>
          </div>
        ) : null}

        {state && state.rooms.length === 0 ? (
          <div className="native-module__state team-status__empty">
            <Users size={24} />
            <strong>No rooms yet</strong>
            <span>Add a meeting or webinar from the Events tab.</span>
          </div>
        ) : null}

        {state && state.rooms.length > 0 ? (
          <div className="team-status__board">
            <table>
              <caption>Current team presence and room coverage</caption>
              <thead>
                <tr>
                  <th scope="col">Team member</th>
                  {state.rooms.map(room => (
                    <th scope="col" key={room.id}>
                      <span className="team-status__room-name">{room.name}</span>
                      {room.is_vip ? <span className="team-status__vip">VIP</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.members.length === 0 ? (
                  <tr><td className="team-status__no-members" colSpan={state.rooms.length + 1}>No team members are assigned yet.</td></tr>
                ) : state.members.map(member => (
                  <tr key={member.id}>
                    <th scope="row">
                      <span className="team-status__member">
                        <span className={`team-status__presence ${member.online ? 'online' : ''}`} aria-label={member.online ? 'Online' : 'Offline'} />
                        <span className={member.online ? '' : 'offline'}>{member.name}</span>
                        {member.id === currentUserId ? <span className="team-status__you">You</span> : null}
                      </span>
                    </th>
                    {state.rooms.map(room => (
                      <CoverageCell key={room.id} assignment={assignments.get(`${member.id}:${room.id}`)} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      ) : null}
    </ModulePage>
  );
}

function Legend({ status, label }: { status: 'active' | 'waiting' | 'error' | 'none'; label: string }) {
  return <span><StatusDot status={status} /><span>{label}</span></span>;
}

function CoverageCell({ assignment }: { assignment: CoverageAssignment | undefined }) {
  const status = assignment?.status && assignment.status in statuses
    ? assignment.status as keyof typeof statuses
    : 'none';
  const label = status === 'none' ? 'Not in this room' : statuses[status].label;
  return (
    <td>
      <span className={`team-status__assignment ${status === 'none' ? 'none' : ''}`}>
        <StatusDot status={status} />
        <span>{assignment?.bot_name || '-'}</span>
        <span className="sr-only">{label}</span>
      </span>
    </td>
  );
}

function StatusDot({ status }: { status: 'active' | 'waiting' | 'error' | 'none' }) {
  return <span className={`team-status__dot ${status}`} aria-hidden="true" />;
}
